import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { ProfilePrinterPanel } from './ProfilePrinterPanel';
import { ConsumedNote } from './ConsumedNote';
import { PrintTabs } from './PrintTabs';
import { loadLastTemplate, saveLastTemplate } from './printerProfile';
import {
  freeformPreview,
  freeformBitmap,
  listTemplates,
  printBlob,
  renderFreeformPdf,
  reportPrinted,
  templateStock,
  type Consumed,
  type FreeformPrintInput,
  type LabelTemplate,
  type SheetMode,
} from './api';

const FIELD_KEYS = ['field_1', 'field_2', 'field_3', 'field_4', 'field_5', 'field_6'] as const;

function nameOf(template: LabelTemplate, language: string): string {
  return language.startsWith('en') ? template.name_en : template.name_ru;
}

/**
 * A printing desk for text that does not belong to a component, place or unit.
 *
 * Unlike the ordinary print queue it keeps no job: the typed message has no
 * inventory identity and is rendered only for the operator's immediate print.
 * The template still provides the physical size, so an XPrinter's roll and a
 * MarkLife tape cannot accidentally share a layout.
 */
export function FreeformPrintPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [templateId, setTemplateId] = useState(() => loadLastTemplate('freeform'));
  const [fields, setFields] = useState<Record<string, string>>({});
  const [copiesInput, setCopiesInput] = useState('1');
  const [mode, setMode] = useState<SheetMode>('labels');
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [spent, setSpent] = useState<Consumed | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const copies = Math.min(200, Math.max(1, Number(copiesInput) || 1));

  const templates = useQuery({
    queryKey: ['labels', 'templates', 'freeform'],
    queryFn: () => listTemplates('freeform'),
  });
  const template = templates.data?.find((item) => item.id === templateId) ?? null;

  useEffect(() => {
    const list = templates.data ?? [];
    if (list.some((item) => item.id === templateId)) return;
    const first = list[0];
    if (first) {
      setTemplateId(first.id);
      saveLastTemplate('freeform', first.id);
    }
  }, [templateId, templates.data]);

  const request = useMemo<FreeformPrintInput | null>(() => templateId ? {
    template_id: templateId,
    fields,
    copies,
    mode,
  } : null, [copies, fields, mode, templateId]);

  const preview = useQuery({
    queryKey: ['labels', 'freeform-preview', request],
    queryFn: () => freeformPreview(request!),
    enabled: request !== null,
  });

  useEffect(() => {
    if (!preview.data) {
      setPreviewUrl(null);
      return undefined;
    }
    const next = URL.createObjectURL(preview.data);
    setPreviewUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [preview.data]);

  const stock = useQuery({
    queryKey: ['labels', 'templates', templateId, 'stock'],
    queryFn: () => templateStock(templateId),
    enabled: Boolean(templateId),
  });

  const printed = useMutation({
    mutationFn: () => reportPrinted(templateId, copies),
    onSuccess: (result) => {
      setConfirming(false);
      setSpent(result);
      void queryClient.invalidateQueries({ queryKey: ['labels', 'templates'] });
      void queryClient.invalidateQueries({ queryKey: ['stock'] });
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const systemPrint = useMutation({
    mutationFn: async () => {
      if (!request) return;
      printBlob(await renderFreeformPdf(request));
    },
    onSuccess: () => {
      setError(null);
      setSpent(null);
      if (mode === 'labels' && stock.data?.component_id) setConfirming(true);
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const setField = (key: string, value: string) => {
    setFields((current) => ({ ...current, [key]: value }));
    setError(null);
  };
  const bitmap = () => freeformBitmap({ template_id: templateId, fields });

  return (
    <div className="print-dialog freeform-print">
      <PrintTabs />
      <div className="station__head">
        <h2>{t('labels.freeform.title')}</h2>
        <span className="hint">{t('labels.freeform.hint')}</span>
      </div>

      <label>
        {t('labels.template')}
        <select value={templateId} onChange={(event) => {
          setTemplateId(event.target.value);
          saveLastTemplate('freeform', event.target.value);
          setCopiesInput('1');
          setConfirming(false);
          setSpent(null);
        }}>
          {(templates.data ?? []).map((item) => (
            <option key={item.id} value={item.id}>
              {nameOf(item, i18n.language)} · {item.media_width_mm}×{item.media_height_mm} {t('labels.mm')}
            </option>
          ))}
        </select>
      </label>

      <div className="designer__grid">
        {FIELD_KEYS.map((key, index) => (
          <label key={key}>
            {t('labels.freeform.field', { number: index + 1 })}
            <input value={fields[key] ?? ''} maxLength={500} onChange={(event) => setField(key, event.target.value)} />
          </label>
        ))}
      </div>
      <p className="hint">{t('labels.freeform.clockHint')}</p>

      {template && (
        <div className="print-dialog__preview">
          {previewUrl && <img src={previewUrl} alt={t('labels.preview')} style={{ aspectRatio: `${template.media_width_mm} / ${template.media_height_mm}`, width: `${Math.min(template.media_width_mm * 4, 320)}px` }} />}
          {preview.isFetching && <span className="hint">{t('common.loading')}</span>}
          <p className="hint">{template.media_width_mm}×{template.media_height_mm} {t('labels.mm')} · {template.dpi} dpi</p>
        </div>
      )}

      <div className="print-dialog__row">
        <label>
          {t('labels.copies')}
          <span className="quantity-stepper">
            <button type="button" onClick={() => setCopiesInput(String(Math.max(1, copies - 1)))} aria-label={t('common.decrease')}>{String.fromCharCode(8722)}</button>
            <input type="number" inputMode="numeric" min={1} max={200} value={copiesInput} onChange={(event) => setCopiesInput(event.target.value)} onBlur={() => setCopiesInput(String(copies))} />
            <button type="button" onClick={() => setCopiesInput(String(Math.min(200, copies + 1)))} aria-label={t('common.increase')}>+</button>
          </span>
        </label>
        {template?.target === 'pdf' && <label>
          {t('labels.mode')}
          <select value={mode} onChange={(event) => setMode(event.target.value as SheetMode)}>
            <option value="labels">{t('labels.modeLabels')}</option>
            <option value="sheet">{t('labels.modeSheet')}</option>
          </select>
        </label>}
      </div>

      {template && request && (
        <ProfilePrinterPanel
          templateId={template.id}
          items={[{ templateId: template.id, copies, medium: { widthMm: template.media_width_mm, heightMm: template.media_height_mm }, loadBitmap: bitmap }]}
          systemBusy={systemPrint.isPending}
          onSystemPrint={() => systemPrint.mutate()}
          onDirectPrinted={async () => { await printed.mutateAsync(); }}
          compact
        />
      )}

      {error && <p className="error">{t(`labels.errors.${error}`, t('labels.errors.unknown'))}</p>}
      {preview.error && <p className="error">{t('labels.errors.unknown')}</p>}
      {spent && <ConsumedNote consumed={spent} />}
      {confirming && <div className="print-dialog__confirm">
        <p>{t('labels.didItPrint', { count: copies })}</p>
        <button type="button" disabled={printed.isPending} onClick={() => printed.mutate()}>{t('labels.printedYes')}</button>
        <button type="button" onClick={() => setConfirming(false)}>{t('labels.printedNoPlain')}</button>
      </div>}
    </div>
  );
}
